// ============================================================
// TOTP-ENROLL — self-serve authenticator-app enrollment for an
// ALREADY SIGNED-IN user (Security page "Enable two-factor" card)
// ============================================================
// Separate from the sign-in-time TOTP flow in auth-cognito.js (which
// operates on a pre-auth Session token during the MFA_SETUP challenge).
// This one runs against a live AccessToken, for a user who wants to
// opt in before MFA becomes mandatory pool-wide.
//
// Mechanics (all AccessToken-authenticated, no Session/challenge involved):
//   1. AssociateSoftwareTokenCommand({ AccessToken }) → secret
//   2. User scans/enters the secret in their authenticator app
//   3. VerifySoftwareTokenCommand({ AccessToken, UserCode, FriendlyDeviceName })
//      → Status: 'SUCCESS'
//   4. SetUserMFAPreferenceCommand({ AccessToken, SoftwareTokenMfaSettings:
//      { Enabled: true, PreferredMfa: true } }) — makes TOTP the factor
//      Cognito will challenge for on every future sign-in.

import {
  CognitoIdentityProviderClient,
  AssociateSoftwareTokenCommand,
  VerifySoftwareTokenCommand,
  SetUserMFAPreferenceCommand,
  GetUserCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { COGNITO_CONFIG } from './runtime-mode.js';
import { getSession } from './auth.js';

let _client = null;
function getClient() {
  if (_client) return _client;
  _client = new CognitoIdentityProviderClient({
    region: COGNITO_CONFIG.userPoolId?.split('_')[0],
  });
  return _client;
}

function accessTokenOrThrow() {
  const session = getSession();
  if (!session?.accessToken) throw new Error('You need to be signed in to manage two-factor authentication.');
  return session.accessToken;
}

/**
 * Is TOTP already the signed-in user's preferred/enabled MFA factor?
 * Returns { enrolled, preferred } — reads live from Cognito, not the JWT
 * (the JWT never carries UserMFASettingList).
 */
export async function getTotpStatus() {
  const accessToken = accessTokenOrThrow();
  const client = getClient();
  const out = await client.send(new GetUserCommand({ AccessToken: accessToken }));
  const settings = out.UserMFASettingList || [];
  return {
    enrolled: settings.includes('SOFTWARE_TOKEN_MFA'),
    preferred: out.PreferredMfaSetting || null,
  };
}

/** Start enrollment — returns { secret, otpauthUrl } for the QR/manual-entry UI. */
export async function beginTotpEnroll() {
  const accessToken = accessTokenOrThrow();
  const session = getSession();
  const client = getClient();
  const out = await client.send(new AssociateSoftwareTokenCommand({ AccessToken: accessToken }));
  const secret = out.SecretCode;
  const issuer = encodeURIComponent('Netcom Connect');
  const account = encodeURIComponent(session?.email || 'account');
  const otpauthUrl = `otpauth://totp/${issuer}:${account}?secret=${secret}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;
  return { secret, otpauthUrl };
}

/** Verify the 6-digit code and, on success, make TOTP the preferred MFA. */
export async function verifyTotpEnroll(code) {
  const trimmed = String(code || '').replace(/\D/g, '');
  if (trimmed.length !== 6) {
    return { success: false, error: 'Enter the 6-digit code from your authenticator app.' };
  }
  const accessToken = accessTokenOrThrow();
  const client = getClient();
  try {
    const verify = await client.send(new VerifySoftwareTokenCommand({
      AccessToken: accessToken,
      UserCode: trimmed,
      FriendlyDeviceName: 'Netcom Connect',
    }));
    if (verify.Status !== 'SUCCESS') {
      return { success: false, error: 'That code was rejected. Check your app and try again.' };
    }
    await client.send(new SetUserMFAPreferenceCommand({
      AccessToken: accessToken,
      SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true },
    }));
    return { success: true };
  } catch (e) {
    const msg = /codemismatch/i.test(e?.name || e?.message || '')
      ? "That code doesn't match. Check your app and try again."
      : (e?.message || 'Enrollment failed.');
    return { success: false, error: msg };
  }
}
